import { ClinicConfig } from '@app/icare/entities/clinic/config'
import { patientClinicQueries } from '@app/icare/entities/clinic/queries/patientClinicQueries'
import { PatientClinicEditDrawer } from '@app/icare/entities/patient/edits/PatientClinicEditDrawer'
import { patientFindingsQueryOptions } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import type { PatientClinicRelation } from '@app/icare/entities/patient/schema/PatientClinic'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { RelationTabs } from '@app/icare/routes/_app/patienten/$id/_edit/-components/RelationTabs'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useRelationCommitNavigation } from '../-components/useRelationCommitNavigation'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_clinic/kliniken')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(patientClinicQueries.forTabs(params.id)),
      queryClient.ensureQueryData(patientFindingsQueryOptions(params.id)),
    ]),
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: relations = [] } = useSuspenseQuery(patientClinicQueries.forTabs(id))
  const { data: findings = [] } = useQuery(patientFindingsQueryOptions(id))
  const { data: totalCount } = useQuery(patientClinicQueries.totalCount(id))

  const handleAfterCommit = useRelationCommitNavigation<PatientClinicRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: relation =>
      navigate({
        to: '/patienten/$id/kliniken/$relation',
        params: { id, relation: relation.id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '.', replace: true }),
  })
  return (
    <RelationTabs
      relations={relations}
      relationTotalCount={totalCount}
      basePath={`/patienten/${id}/kliniken`}
      findings={findings}
      findingsGroup="clinics"
      getLabel={item => ClinicConfig.format.primary(item.to) ?? t`Klinik`}
      onAdd={params =>
        void PatientClinicEditDrawer.show({ patientId: id, onAfterCommit: handleAfterCommit, ...params })
      }
      entity="clinic"
    />
  )
}
